import { useState, useEffect } from 'react';
import { Bell, X, ExternalLink, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import apiService from '@/services/api';
import { cn } from '@/lib/utils';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  metadata?: {
    quote_id?: number;
    invoice_number?: string;
    amount_gbp?: string;
    amount_pence?: number;
    service_name?: string;
    payment_link?: string;
    status?: string;
  };
  read_at?: string;
  created_at: string;
}

interface NotificationSummary {
  total_notifications: number;
  unread_count: number;
}

const NotificationBell = () => {
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [summary, setSummary] = useState<NotificationSummary>({
    total_notifications: 0,
    unread_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [expandedNotification, setExpandedNotification] = useState<Notification | null>(null);

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!isAuthenticated || user?.role !== 'customer') {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.request('/customer/notifications', 'GET');
      
      if (response.success && response.data) {
        setNotifications(response.data);
      }

      if (response.summary) {
        setSummary(response.summary);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications on mount and set up polling
  useEffect(() => {
    fetchNotifications();

    // Poll for notifications every 10 seconds
    const pollInterval = setInterval(() => {
      fetchNotifications();
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [isAuthenticated, user?.role]);

  // Mark notification as read
  const markAsRead = async (notificationId: number) => {
    try {
      await apiService.request(`/notifications/${notificationId}/read`, 'PUT');
      
      // Update local state
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId
            ? { ...notif, read_at: new Date().toISOString() }
            : notif
        )
      );
      
      // Update unread count
      setSummary((prev) => ({
        ...prev,
        unread_count: Math.max(0, prev.unread_count - 1),
      }));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId: number) => {
    try {
      // Soft delete - just hide it locally
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setSummary((prev) => ({
        ...prev,
        total_notifications: Math.max(0, prev.total_notifications - 1),
        unread_count: Math.max(
          0,
          prev.unread_count - (notifications.find((n) => n.id === notificationId)?.read_at ? 0 : 1)
        ),
      }));
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  // If not authenticated or not a customer, don't show notification bell
  if (!isAuthenticated || user?.role !== 'customer') {
    return null;
  }

  const hasUnread = summary.unread_count > 0;

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative w-10 h-10 p-0"
          title={`${summary.unread_count} unread notifications`}
        >
          <Bell className="h-5 w-5" />
          {hasUnread && (
            <span className="absolute top-0 right-0 inline-flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-red-500 rounded-full">
              {summary.unread_count > 9 ? '9+' : summary.unread_count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-96 max-h-96 overflow-y-auto p-0">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Notifications</h2>
            <span className="text-xs text-muted-foreground">
              {summary.unread_count} new
            </span>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No notifications yet
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  'p-4 transition-colors hover:bg-muted',
                  !notification.read_at && 'bg-blue-50/50 dark:bg-blue-950/20'
                )}
              >
                <div className="flex items-start gap-3">
                  {/* Unread indicator */}
                  {!notification.read_at && (
                    <div className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                  )}

                  {/* Notification content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {notification.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {notification.message}
                    </p>

                    {/* Quote Details if available */}
                    {notification.metadata?.quote_id && (
                      <div className="mt-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                        <p>Quote #{notification.metadata.quote_id}</p>
                        {notification.metadata.amount_gbp && (
                          <p>Amount: ${notification.metadata.amount_gbp}</p>
                        )}
                        {notification.metadata.service_name && (
                          <p>Service: {notification.metadata.service_name}</p>
                        )}
                      </div>
                    )}

                    {/* Timestamp */}
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(notification.created_at).toLocaleDateString('en-GB', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>

                    {/* Action Buttons */}
                    <div className="mt-2 flex gap-2">
                      {/* View Full Notification */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-6"
                        onClick={() => {
                          markAsRead(notification.id);
                          setExpandedNotification(notification);
                        }}
                      >
                        <ChevronRight className="h-3 w-3 mr-1" />
                        View
                      </Button>

                      {notification.metadata?.payment_link && (
                        <Button
                          size="sm"
                          variant="default"
                          className="text-xs h-6"
                          asChild
                        >
                          <a href={notification.metadata.payment_link} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Pay Now
                          </a>
                        </Button>
                      )}

                      {!notification.read_at && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-6"
                          onClick={() => markAsRead(notification.id)}
                        >
                          Mark as Read
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Dismiss button */}
                  <button
                    className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                    onClick={() => dismissNotification(notification.id)}
                    aria-label="Dismiss notification"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="sticky bottom-0 bg-card border-t p-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs h-8"
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Expanded Notification Modal */}
    {expandedNotification && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border rounded-lg shadow-lg max-w-2xl w-full max-h-96 overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-card border-b p-6 flex items-center justify-between">
            <h2 className="text-lg font-semibold">{expandedNotification.title}</h2>
            <button
              onClick={() => setExpandedNotification(null)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Message */}
            <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap text-sm">
              {expandedNotification.message}
            </div>

            {/* Metadata Details */}
            {expandedNotification.metadata && Object.keys(expandedNotification.metadata).length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">Details:</h3>
                <div className="bg-muted/30 p-4 rounded-lg space-y-2 text-sm">
                  {expandedNotification.metadata.quote_id && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Quote ID:</span>
                      <span className="font-medium">#{expandedNotification.metadata.quote_id}</span>
                    </div>
                  )}
                  {expandedNotification.metadata.invoice_number && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Invoice:</span>
                      <span className="font-medium">{expandedNotification.metadata.invoice_number}</span>
                    </div>
                  )}
                  {expandedNotification.metadata.amount_gbp && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="font-medium">{expandedNotification.metadata.amount_gbp}</span>
                    </div>
                  )}
                  {expandedNotification.metadata.service_name && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Service:</span>
                      <span className="font-medium">{expandedNotification.metadata.service_name}</span>
                    </div>
                  )}
                  {expandedNotification.metadata.status && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <span className="font-medium capitalize">{expandedNotification.metadata.status.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timestamp */}
            <div className="text-xs text-muted-foreground pt-2 border-t">
              {new Date(expandedNotification.created_at).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>

          {/* Footer - Action Buttons */}
          <div className="sticky bottom-0 bg-card border-t p-4 flex gap-2">
            {expandedNotification.metadata?.payment_link && (
              <Button
                className="flex-1"
                asChild
              >
                <a href={expandedNotification.metadata.payment_link} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Proceed to Payment
                </a>
              </Button>
            )}
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setExpandedNotification(null)}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default NotificationBell;
